import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabase";
import { useNavigate, Link } from "react-router-dom";
import { friendlyError } from "../utils/errors";
import { PASSWORD_REQUIREMENTS_TEXT, isPasswordValid } from "../utils/password";
import { useLanguage } from "../context/LanguageContext";
import PickerSelect from "../components/PickerSelect";
import Turnstile from "../components/Turnstile";
import { withTimeout } from "../utils/timedFetch";
import { trackFunnelEvent } from "../utils/funnelTrack";
import {
  IoEyeOffOutline,
  IoEyeOutline,
  IoCheckmarkCircle,
  IoWarningOutline,
  IoShieldCheckmarkOutline,
  IoCheckmark,
} from "react-icons/io5";

const USERNAME_REGEX = /^[a-z0-9._]{3,20}$/;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// month is 1-12; day 0 of the *next* month is the last day of this one
function daysInMonth(month, year) {
  return new Date(year, month, 0).getDate();
}

// Sending the code goes through the SMTP provider before Supabase answers,
// which can legitimately take longer than an ordinary request.
const OTP_SEND_TIMEOUT_MS = 15000;

// A timed-out request may still have gone through on the server (we only stop
// waiting for it). Retrying then hits "code already used" / "same password",
// leaving the person stuck on a step that in fact already succeeded — so
// before treating a failure as final, check whether the session says it did.
async function isSignedInAs(email) {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.user?.email?.toLowerCase() === email.trim().toLowerCase();
  } catch {
    return false;
  }
}

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "prefer_not_to_say", label: "Prefer not to say" }
];

export default function Login() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Top-level flow: "login" | "signup" | "recovery" — remembers just the
  // login/signup tab choice across a reload (not "recovery", which is only
  // ever entered from within login and shouldn't survive as a landing state).
  const [flow, setFlow] = useState(() => {
    try {
      return localStorage.getItem("virsa_auth_tab") === "signup" ? "signup" : "login";
    } catch {
      return "login";
    }
  });

  useEffect(() => {
    if (flow !== "login" && flow !== "signup") return;
    try {
      localStorage.setItem("virsa_auth_tab", flow);
    } catch {
      // private mode / blocked storage — the tab just won't stick, no big deal
    }
  }, [flow]);

  // Referral capture (?ref=<username>) happens once at the app root in
  // App.js, since the link people actually share points at /install or a
  // design page, not always /login — see that file's comment. Read back
  // from sessionStorage at signup completion in finishProfileCreation below.

  // Verifying the signup OTP already creates a real, authenticated session —
  // reloading or navigating away right after that (before username/gender/
  // password) would otherwise leave someone "signed in" app-wide with no
  // password ever set and no way back in later. Resume right where the
  // wizard left off instead of showing the plain sign-in form. Birthdate is
  // asked again since, unlike email, it was never saved anywhere until the
  // final step.
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(async ({ data }) => {
      const session = data.session;
      if (!session?.user || !mounted) return;

      const { data: profile, error } = await supabase
        .from("users")
        .select("id")
        .eq("id", session.user.id)
        .maybeSingle();

      if (!mounted || error || profile) return;

      setFlow("signup");
      setSignupEmail(session.user.email || "");
      // Google (or any future OAuth provider) already verified this person —
      // Supabase's own auth handles their credentials, so the wizard skips
      // straight from gender to finishing instead of also collecting a
      // GatherYou-specific password. "email" here means the OTP+password
      // flow, not a literal check for Gmail.
      setIsOAuthAccount(session.user.app_metadata?.provider !== "email");
      setSignupStage("birthdate");
      trackFunnelEvent("signup_birthdate_reached");
    }).catch(() => {
      // Session lookup failed — just show the normal form.
    });
    return () => {
      mounted = false;
    };
  }, []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [isOAuthAccount, setIsOAuthAccount] = useState(false);

  // Shared across the sign-in, signup and recovery forms — only one is ever
  // visible at a time, matching the existing single `showPassword` flag
  // below. Cleared (and the widget reset) after every submit attempt since
  // Turnstile tokens are single-use.
  const [captchaToken, setCaptchaToken] = useState(null);
  const turnstileRef = useRef(null);
  function resetCaptcha() {
    setCaptchaToken(null);
    turnstileRef.current?.reset();
  }

  function resetMessages() {
    setError(null);
    setNotice(null);
  }

  // `loading` only disables a button after the next render, so a very fast
  // double-tap can still run a handler twice — two codes emailed (the first
  // then invalid), or a second verify of an already-used code overwriting the
  // first one's success with "That code didn't work". A ref flips instantly.
  const busyRef = useRef(false);
  function once(handler) {
    return async event => {
      if (busyRef.current) {
        event?.preventDefault?.();
        return;
      }
      busyRef.current = true;
      try {
        await handler(event);
      } finally {
        busyRef.current = false;
      }
    };
  }

  // ================= GOOGLE OAUTH =================
  // Works for both sign-in and sign-up with one button: Login.jsx's own
  // session-check effect (above) already resumes the signup wizard at
  // "birthdate" whenever a session exists with no `public.users` row yet,
  // regardless of which provider created that session — Google needs no
  // separate handling for "new" vs "existing" account.
  const [googleLoading, setGoogleLoading] = useState(false);
  async function signInWithGoogle() {
    resetMessages();
    setGoogleLoading(true);
    trackFunnelEvent("signup_google_clicked");
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin }
    });
    // Only reachable if Supabase rejected the request before ever
    // redirecting (e.g. Google isn't configured yet) — a successful call
    // navigates the whole page away, so there's nothing to reset back to.
    if (oauthError) {
      setGoogleLoading(false);
      setError(friendlyError(oauthError, "Couldn't start Google sign-in. Try again."));
    }
  }

  // ================= LOGIN =================
  const [loginStage, setLoginStage] = useState("id"); // "id" | "secret"
  const [loginId, setLoginId] = useState(""); // email
  const [resolvedEmail, setResolvedEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const passwordRef = useRef(null);

  useEffect(() => {
    if (loginStage === "secret" && passwordRef.current) passwordRef.current.focus();
  }, [loginStage]);

  // Email-only, deliberately — this used to also accept a username and
  // resolve it to an email via a get_email_by_username RPC, but that RPC had
  // to be callable by anon (login happens before you're authenticated), which
  // meant anyone could call it directly (bypassing this UI entirely) with any
  // username and get back that account's real email address. Usernames are
  // public, so that made every account's email trivially harvestable. There's
  // no way to resolve username → email for a pre-auth login without exposing
  // that mapping to an unauthenticated caller somewhere, so the fix is to not
  // need the resolution at all.
  async function handleLoginIdSubmit(e) {
    e.preventDefault();
    resetMessages();

    const value = loginId.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError("Enter the email on your account.");
      return;
    }

    setLoading(true);
    let exists, checkError;
    try {
      ({ data: exists, error: checkError } = await withTimeout(
        supabase.rpc("email_exists", { check_email: value }),
        8000
      ));
    } catch (timeoutErr) {
      // Fail open — a broken check here shouldn't block a real account from
      // signing in, just skip straight to the password step as before.
      setLoading(false);
      setResolvedEmail(value);
      setLoginStage("secret");
      return;
    }
    setLoading(false);

    if (checkError) {
      // Fail open — a broken check here shouldn't block a real account from
      // signing in, just skip straight to the password step as before.
      console.log("LOGIN EMAIL CHECK ERROR:", checkError);
      setResolvedEmail(value);
      setLoginStage("secret");
      return;
    }

    if (!exists) {
      setError("No GatherYou account uses that email yet.");
      return;
    }

    setResolvedEmail(value);
    setLoginStage("secret");
  }

  async function handleSignIn(e) {
    e.preventDefault();
    resetMessages();
    setLoading(true);

    let error;
    try {
      ({ error } = await withTimeout(
        supabase.auth.signInWithPassword({ email: resolvedEmail, password, options: { captchaToken } }),
        8000
      ));
    } catch (timeoutErr) {
      setLoading(false);
      resetCaptcha();
      setError("Couldn't sign in right now. Try again.");
      return;
    }

    setLoading(false);
    resetCaptcha();

    if (error) {
      setError(friendlyError(error, "Couldn't sign in. Try again."));
      return;
    }

    navigate("/");
  }

  function changeLoginId() {
    setLoginStage("id");
    setPassword("");
    resetMessages();
    resetCaptcha();
  }

  function goToRecovery() {
    setFlow("recovery");
    setRecoveryEmail(resolvedEmail || "");
    setRecoveryStage("email");
    resetMessages();
    resetCaptcha();
  }

  // ================= SIGNUP =================
  const [signupStage, setSignupStage] = useState("email"); // email -> otp -> birthdate -> username -> gender -> password -> done
  const [signupEmail, setSignupEmail] = useState("");
  const [signupOtp, setSignupOtp] = useState("");
  const [birthdate, setBirthdate] = useState(""); // "YYYY-MM-DD" once day, month and year are all picked
  const [dobParts, setDobParts] = useState({ d: "", m: "", y: "" });
  const [username, setUsername] = useState("");
  const [usernameStatus, setUsernameStatus] = useState("idle"); // idle|checking|available|taken|invalid
  const [usernameSuggestions, setUsernameSuggestions] = useState([]);
  const [gender, setGender] = useState(null); // "male" | "female" | "prefer_not_to_say" | null (skipped)
  const [signupPassword, setSignupPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  async function sendSignupOtp(e) {
    e.preventDefault();
    resetMessages();

    const cleanEmail = signupEmail.trim().toLowerCase();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError("Enter a valid email address.");
      return;
    }

    // The checkbox below disables the button until this is checked, but
    // that's just UI state — nothing stops a form submit from firing some
    // other way (pressing Enter before React re-renders the disabled
    // state, for one), so this needs its own check regardless.
    if (!agreedToTerms) {
      setError("Please agree to the Terms of Service and Privacy Policy to continue.");
      return;
    }

    setLoading(true);

    // Block signup if this email already has an account (checked via a secure RPC
    // so the client never needs direct SELECT on the email column)
    let emailTaken, existingError;
    try {
      ({ data: emailTaken, error: existingError } = await withTimeout(
        supabase.rpc("email_exists", { check_email: cleanEmail }),
        8000
      ));
    } catch (timeoutErr) {
      setLoading(false);
      setError("Couldn't verify that email right now. Try again.");
      return;
    }

    if (existingError) {
      setLoading(false);
      console.log("EMAIL CHECK ERROR:", existingError);
      setError("Couldn't verify that email right now. Try again.");
      return;
    }

    if (emailTaken) {
      setLoading(false);
      setError("An account already uses this email. Try signing in instead.");
      return;
    }

    setSignupEmail(cleanEmail);

    let error;
    try {
      ({ error } = await withTimeout(
        supabase.auth.signInWithOtp({
          email: cleanEmail,
          options: { shouldCreateUser: true, captchaToken }
        }),
        OTP_SEND_TIMEOUT_MS
      ));
    } catch (timeoutErr) {
      setLoading(false);
      resetCaptcha();
      setError("Couldn't send the code right now. Try again.");
      return;
    }
    setLoading(false);
    resetCaptcha();

    if (error) {
      setError(friendlyError(error, "Couldn't send the code. Try again."));
      return;
    }

    // "Resend code" calls this same function without leaving the OTP stage —
    // without clearing it, a stale code already typed from the first email
    // stays sitting in the field. Requesting a new code invalidates the old
    // one, so submitting that leftover value again just fails with "that
    // code didn't work," even though the user did nothing wrong.
    setSignupOtp("");
    setNotice(`We sent a 6-digit code to ${cleanEmail}.`);
    setSignupStage("otp");
    trackFunnelEvent("signup_otp_sent");
  }

  async function verifySignupOtp(e) {
    e.preventDefault();
    resetMessages();

    if (signupOtp.trim().length < 6) {
      setError("Enter the 6-digit code from your email.");
      return;
    }

    setLoading(true);
    let error;
    try {
      ({ error } = await withTimeout(
        supabase.auth.verifyOtp({
          email: signupEmail,
          token: signupOtp.trim(),
          type: "email"
        }),
        8000
      ));
    } catch (timeoutErr) {
      setLoading(false);
      setError("Couldn't verify that code right now. Try again.");
      return;
    }
    setLoading(false);

    if (error) {
      // A first attempt that timed out client-side may still have succeeded;
      // the retry then fails because that code is already used.
      if (await isSignedInAs(signupEmail)) {
        setSignupStage("birthdate");
        trackFunnelEvent("signup_birthdate_reached");
        return;
      }
      setError("That code didn't work. Check it and try again.");
      return;
    }

    setSignupStage("birthdate");
    trackFunnelEvent("signup_birthdate_reached");
  }

  // Three dropdowns instead of <input type="date">: the native picker opens on
  // today's date, so reaching a birth year 18+ years back meant tapping the
  // month arrow hundreds of times.
  function changeDobPart(part, value) {
    const next = { ...dobParts, [part]: value };

    // A day that doesn't exist in the newly picked month (31 → February)
    // would silently roll over into the next month — clear it instead.
    // 2000 stands in for the year until one is picked, so 29 Feb stays allowed.
    if (next.d && next.m && Number(next.d) > daysInMonth(Number(next.m), Number(next.y) || 2000)) {
      next.d = "";
    }

    setDobParts(next);

    if (next.d && next.m && next.y) {
      setBirthdate(`${next.y}-${String(next.m).padStart(2, "0")}-${String(next.d).padStart(2, "0")}`);
    } else {
      setBirthdate("");
    }
  }

  // India's DPDP Act treats anyone under 18 as a child, needing verifiable
  // parental consent to have their data processed at all — a mechanism this
  // app has no way to actually verify. The practical route most apps this
  // size take is to just require 18+ outright instead of building that.
  // Enforced again server-side in complete_signup_profile — this check alone
  // wouldn't stop someone calling that RPC directly.
  function submitBirthdateStage(e) {
    e.preventDefault();
    resetMessages();

    if (!birthdate) {
      setError("Enter your date of birth.");
      return;
    }

    const [y, m, d] = birthdate.split("-").map(Number);
    const turns18 = new Date(y + 18, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (turns18 > today) {
      setError("You must be at least 18 years old to use GatherYou.");
      return;
    }

    setSignupStage("username");
    trackFunnelEvent("signup_username_reached");
  }

  // Debounced live username availability check
  const usernameCheckIdRef = useRef(0);
  useEffect(() => {
    if (signupStage !== "username") return;
    if (!username) {
      setUsernameStatus("idle");
      return;
    }

    const clean = username.trim().toLowerCase();

    if (!USERNAME_REGEX.test(clean)) {
      setUsernameStatus("invalid");
      setUsernameSuggestions([]);
      return;
    }

    setUsernameStatus("checking");
    const requestId = ++usernameCheckIdRef.current;
    const controller = new AbortController();
    // A slow/stuck token-refresh under the hood (or just a bad connection)
    // can leave this awaiting forever with no error — abort it after a
    // while so "Checking..." doesn't sit on screen with no way out.
    const abortTimer = setTimeout(() => controller.abort(), 8000);

    const timer = setTimeout(async () => {
      try {
        const { data, error: checkError } = await supabase
          .from("users")
          .select("id")
          .eq("username", clean)
          .abortSignal(controller.signal)
          .maybeSingle();

        // A slower, older check can resolve after a newer one already did —
        // ignore it so it can't overwrite the status for what's now typed.
        if (requestId !== usernameCheckIdRef.current) return;

        if (checkError) {
          console.log("USERNAME CHECK ERROR:", checkError);
          setUsernameStatus("error");
          setUsernameSuggestions([]);
          return;
        }

        if (data) {
          setUsernameStatus("taken");
          setUsernameSuggestions([
            `${clean}${Math.floor(Math.random() * 90 + 10)}`,
            `${clean}_${Math.floor(Math.random() * 900 + 100)}`,
            `the_${clean}`
          ]);
        } else {
          setUsernameStatus("available");
          setUsernameSuggestions([]);
        }
      } catch (err) {
        if (requestId !== usernameCheckIdRef.current) return;
        console.log("USERNAME CHECK ERROR:", err);
        setUsernameStatus("error");
        setUsernameSuggestions([]);
      } finally {
        clearTimeout(abortTimer);
      }
    }, 450);

    return () => {
      clearTimeout(timer);
      clearTimeout(abortTimer);
      controller.abort();
    };
  }, [username, signupStage]);

  function submitUsernameStage(e) {
    e.preventDefault();
    resetMessages();

    if (usernameStatus !== "available") {
      setError("Pick a username that's available first.");
      return;
    }

    setSignupStage("gender");
    trackFunnelEvent("signup_gender_reached");
  }

  // Google/OAuth accounts have no GatherYou password to collect, so they
  // finish right from the gender stage; email accounts go on to "password"
  // and finish from completeSignup below instead. `genderValue` is taken as
  // an argument rather than read from the `gender` state closure because the
  // "Skip" button below calls setGender(null) and this in the same tick —
  // state setters don't apply until the next render, so reading `gender`
  // here would still see the previous value.
  function continueFromGenderStage(genderValue) {
    resetMessages();
    if (isOAuthAccount) {
      finishProfileCreation(genderValue);
    } else {
      setSignupStage("password");
      trackFunnelEvent("signup_password_reached");
    }
  }

  // Via RPC, not a direct table upsert — `email` isn't grantable to
  // `authenticated` for SELECT (so other users can't read anyone's email
  // via a plain .select()), but PostgREST's upsert needs SELECT on every
  // payload column regardless of the Prefer header, which meant this
  // failed with "permission denied for table users" for every signup.
  // The RPC runs as its owner (bypassing that grant) and still does the
  // same upsert-by-id underneath, so a retry after a lost response still
  // won't hit a false "username taken" on the id column.
  async function finishProfileCreation(genderValue) {
    setLoading(true);

    const cleanUsername = username.trim().toLowerCase();
    let referredByUsername = null;
    try {
      referredByUsername = sessionStorage.getItem("gatheryou_referred_by");
    } catch {
      // private mode / blocked storage — no referral to credit
    }

    let profileError;
    try {
      ({ error: profileError } = await withTimeout(
        supabase.rpc("complete_signup_profile", {
          p_username: cleanUsername,
          p_email: signupEmail,
          p_gender: genderValue,
          p_birthdate: birthdate,
          p_referred_by_username: referredByUsername
        }),
        8000
      ));

      // PGRST202: PostgREST couldn't find a function matching this exact set
      // of named parameters — happens if referral-system.sql hasn't been run
      // yet live (the old 4-parameter version is still what's deployed).
      // Retrying without the referral parameter must not be allowed to break
      // every signup over one missing migration; the referral itself is just
      // not credited this time.
      if (profileError?.code === "PGRST202") {
        ({ error: profileError } = await withTimeout(
          supabase.rpc("complete_signup_profile", {
            p_username: cleanUsername,
            p_email: signupEmail,
            p_gender: genderValue,
            p_birthdate: birthdate
          }),
          8000
        ));
      }
    } catch (timeoutErr) {
      setLoading(false);
      setError("Couldn't finish creating your account right now. Try again.");
      return;
    }

    setLoading(false);

    if (profileError) {
      console.log("CREATE PROFILE ERROR:", profileError);
      if (profileError.code === "23505") {
        setError("That username was just taken — pick another one.");
        setSignupStage("username");
        setUsername("");
        return;
      }
      if (profileError.message?.includes("must_be_18_or_older")) {
        setError("You must be at least 18 years old to use GatherYou.");
        setSignupStage("birthdate");
        return;
      }
      setError(friendlyError(profileError, "Couldn't finish creating your account. Try again."));
      return;
    }

    trackFunnelEvent("signup_completed");

    // App gates every page on this account having a users row, and nothing
    // else tells it one just appeared — without this, "Go to GatherYou"
    // below lands back on the signup form instead of the home page.
    window.dispatchEvent(new Event("gatheryou:profile-created"));

    // The signup tab is only remembered so a reload mid-signup doesn't drop
    // someone back on "Sign in" — once it's finished, later visits (after a
    // logout, say) should open on "Sign in" again, not "Create account".
    try {
      localStorage.setItem("virsa_auth_tab", "login");
    } catch {
      // storage blocked — nothing to reset
    }

    setSignupStage("done");
  }

  async function completeSignup(e) {
    e.preventDefault();
    resetMessages();

    if (!isPasswordValid(signupPassword)) {
      setError(PASSWORD_REQUIREMENTS_TEXT);
      return;
    }

    setLoading(true);

    let pwError;
    try {
      ({ error: pwError } = await withTimeout(
        supabase.auth.updateUser({ password: signupPassword }),
        8000
      ));
    } catch (timeoutErr) {
      setLoading(false);
      setError("Couldn't create your account right now. Try again.");
      return;
    }

    // "Same password" means a previous attempt already set exactly this
    // password (it timed out on our side but went through) — the state we
    // want, so carry on to creating the profile instead of blocking here.
    if (pwError && pwError.code !== "same_password") {
      setLoading(false);
      setError(friendlyError(pwError, "Couldn't create your account. Try again."));
      return;
    }

    await finishProfileCreation(gender);
  }

  // ================= RECOVERY =================
  const [recoveryStage, setRecoveryStage] = useState("email"); // email -> otp -> newpassword -> done
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryOtp, setRecoveryOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");

  async function sendRecoveryOtp(e) {
    e.preventDefault();
    resetMessages();

    // Every other email entry point (handleLoginIdSubmit, sendSignupOtp)
    // normalizes before validating/sending — this one didn't, so a user
    // typing/pasting a different case than they signed up with got the
    // deliberately-vague "code sent" success message but never actually
    // received one, with no way to tell why.
    const cleanEmail = recoveryEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError("Enter the email on your account.");
      return;
    }
    setRecoveryEmail(cleanEmail);

    setLoading(true);
    let error;
    try {
      ({ error } = await withTimeout(
        supabase.auth.signInWithOtp({
          email: cleanEmail,
          options: { shouldCreateUser: false, captchaToken }
        }),
        OTP_SEND_TIMEOUT_MS
      ));
    } catch (timeoutErr) {
      setLoading(false);
      resetCaptcha();
      setError("Couldn't send the code right now. Try again.");
      return;
    }
    setLoading(false);
    resetCaptcha();

    // shouldCreateUser: false makes Supabase respond with an error for an
    // email that has no account, but silent success for one that does —
    // showing that error directly would let anyone probe which emails are
    // registered here at all, the same class of leak the login flow's old
    // get_email_by_username RPC had (see handleLoginIdSubmit above). Any
    // other error (network, rate limit) still needs to reach the user, but
    // this specific one is treated the same as success — proceeding to the
    // OTP stage either way, since an unregistered email just never receives
    // a code — so the response looks identical regardless of whether the
    // account exists.
    if (error && !/user not found|not allowed|no user found/i.test(error.message || "")) {
      setError(friendlyError(error, "Couldn't send the code. Try again."));
      return;
    }

    // Same reason as sendSignupOtp: "Resend code" calls this without leaving
    // the OTP stage, so a stale code from the previous email would otherwise
    // sit in the field and fail once the new code invalidates it.
    setRecoveryOtp("");
    setNotice(`If an account exists for ${cleanEmail}, we've sent a recovery code.`);
    setRecoveryStage("otp");
  }

  async function verifyRecoveryOtp(e) {
    e.preventDefault();
    resetMessages();

    if (recoveryOtp.trim().length < 6) {
      setError("Enter the 6-digit code from your email.");
      return;
    }

    setLoading(true);
    let error;
    try {
      ({ error } = await withTimeout(
        supabase.auth.verifyOtp({
          email: recoveryEmail,
          token: recoveryOtp.trim(),
          type: "email"
        }),
        8000
      ));
    } catch (timeoutErr) {
      setLoading(false);
      setError("Couldn't verify that code right now. Try again.");
      return;
    }
    setLoading(false);

    if (error) {
      setError("That code didn't work. Check it and try again.");
      return;
    }

    setRecoveryStage("newpassword");
  }

  async function submitNewPassword(e) {
    e.preventDefault();
    resetMessages();

    if (!isPasswordValid(newPassword)) {
      setError(PASSWORD_REQUIREMENTS_TEXT);
      return;
    }

    setLoading(true);
    let error;
    try {
      ({ error } = await withTimeout(
        supabase.auth.updateUser({ password: newPassword }),
        8000
      ));
    } catch (timeoutErr) {
      setLoading(false);
      setError("Couldn't reset your password right now. Try again.");
      return;
    }
    setLoading(false);

    if (error) {
      setError(friendlyError(error, "Couldn't update your password. Try again."));
      return;
    }

    setRecoveryStage("done");
  }

  // One "show password" flag serves the sign-in, signup and recovery fields —
  // without this, turning it on for one leaves the next password screen
  // showing its text in the clear too. (Declared after every stage's state,
  // since the dependency list below reads them all during render.)
  useEffect(() => {
    setShowPassword(false);
  }, [flow, loginStage, signupStage, recoveryStage]);

  function backToLoginFromRecovery() {
    setFlow("login");
    setLoginStage("id");
    resetMessages();
    resetCaptcha();
  }

  return (
    <div style={page}>
      <style>{`
        * { box-sizing: border-box; }
        .virsa-card { animation: virsaRise 0.5s cubic-bezier(0.16,1,0.3,1); }
        .virsa-stage { animation: virsaSlide 0.35s cubic-bezier(0.16,1,0.3,1); }
        .virsa-input:focus { border-color: var(--accent-primary) !important; }
        .virsa-btn-primary:hover { filter: brightness(1.06); }
        .virsa-btn-primary:disabled { opacity: 0.6; cursor: default; }
        .virsa-otp { letter-spacing: 8px; font-size: 20px; text-align: center; font-weight: 700; }
        .virsa-tab { transition: color .15s, border-color .15s; }
        @keyframes virsaRise { from{opacity:0;transform:translateY(14px);} to{opacity:1;transform:translateY(0);} }
        @keyframes virsaSlide { from{opacity:0;transform:translateX(10px);} to{opacity:1;transform:translateX(0);} }
        @media (prefers-reduced-motion: reduce) { .virsa-card,.virsa-stage{animation:none;} }
      `}</style>

      <div style={card} className="virsa-card">
        <div style={trimRow} aria-hidden="true">
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} style={{ ...trimDiamond, background: i % 2 === 0 ? "var(--accent-primary)" : "var(--accent-gold)" }} />
          ))}
        </div>

        <div style={cardBody}>
          <div style={brandRow}>
            <img src="/brand/logo-mark.png" alt="" width={32} height={32} style={brandIcon} />
            <span style={brandMark}>GatherYou</span>
          </div>

          {/* ===== Tabs (only on first step of login / signup) ===== */}
          {((flow === "login" && loginStage === "id") ||
            (flow === "signup" && signupStage === "email")) && (
            <div style={tabRow}>
              <button
                className="virsa-tab"
                style={{ ...tabBtn, ...(flow === "login" ? tabBtnActive : {}) }}
                onClick={() => {
                  setFlow("login");
                  resetMessages();
                  resetCaptcha();
                }}
                disabled={loading}
              >
                {t("login.signIn")}
              </button>
              <button
                className="virsa-tab"
                style={{ ...tabBtn, ...(flow === "signup" ? tabBtnActive : {}) }}
                onClick={() => {
                  setFlow("signup");
                  resetMessages();
                  resetCaptcha();
                  trackFunnelEvent("signup_started");
                }}
                disabled={loading}
              >
                {t("login.signUp")}
              </button>
            </div>
          )}

          {/* Google covers both sign-in and sign-up — same button either way,
              see signInWithGoogle's own comment above. */}
          {((flow === "login" && loginStage === "id") ||
            (flow === "signup" && signupStage === "email")) && (
            <>
              <button
                type="button"
                style={googleBtn}
                onClick={once(signInWithGoogle)}
                disabled={loading || googleLoading}
              >
                <GoogleGlyph />
                {googleLoading ? "Redirecting…" : "Continue with Google"}
              </button>
              <div style={dividerRow}>
                <span style={dividerLine} />
                <span style={dividerText}>or</span>
                <span style={dividerLine} />
              </div>
            </>
          )}

          {/* ===================== LOGIN ===================== */}
          {flow === "login" && loginStage === "id" && (
            <div className="virsa-stage" key="login-id">
              <h1 style={heading}>{t("login.welcomeBack")}</h1>
              <p style={subheading}>{t("login.signInSubtitle")}</p>

              {error && (
                <AlertBox tone="error">
                  {error}
                  {error.includes("No GatherYou account") && (
                    <>
                      {" "}
                      <button
                        type="button"
                        style={{ ...linkBtn, textDecoration: "underline" }}
                        onClick={() => {
                          setSignupEmail(loginId.trim().toLowerCase());
                          setFlow("signup");
                          setSignupStage("email");
                          resetMessages();
                          trackFunnelEvent("signup_started");
                        }}
                      >
                        Create account instead
                      </button>
                    </>
                  )}
                </AlertBox>
              )}

              <form onSubmit={once(handleLoginIdSubmit)}>
                <label style={fieldLabel} htmlFor="login-id">{t("login.usernameOrEmail")}</label>
                <input
                  id="login-id"
                  className="virsa-input"
                  style={input}
                  type="email"
                  autoFocus
                  placeholder="you@example.com"
                  value={loginId}
                  onChange={e => setLoginId(e.target.value)}
                />
                <button className="virsa-btn-primary" style={primaryBtn} disabled={loading}>
                  {loading ? "Checking…" : t("login.continue")}
                </button>
              </form>
            </div>
          )}

          {flow === "login" && loginStage === "secret" && (
            <div className="virsa-stage" key="login-secret">
              <button style={backLink} onClick={changeLoginId} disabled={loading}>← Back</button>

              <div style={motifWrap}>
                <FlowerMotif />
                <div style={avatarCircle}>{resolvedEmail?.[0]?.toUpperCase() || "?"}</div>
              </div>

              <button style={idChip} onClick={changeLoginId} disabled={loading}>
                {resolvedEmail} <span style={{ color: "var(--accent-link)", fontWeight: 600 }}>Change</span>
              </button>

              <h1 style={{ ...heading, marginTop: 18 }}>Enter your password</h1>
              {error && <AlertBox tone="error">{error}</AlertBox>}

              <form onSubmit={once(handleSignIn)}>
                <label style={fieldLabel} htmlFor="login-password">{t("login.password")}</label>
                <div style={passwordWrap}>
                  <input
                    id="login-password"
                    ref={passwordRef}
                    className="virsa-input"
                    style={{ ...input, marginBottom: 0 }}
                    type={showPassword ? "text" : "password"}
                    placeholder="Your password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                  />
                  <button type="button" style={eyeBtn} onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? <IoEyeOffOutline /> : <IoEyeOutline />}
                  </button>
                </div>

                <button type="button" style={forgotLink} onClick={goToRecovery} disabled={loading}>
                  Forgot password?
                </button>

                <AlertBox tone="security">
                  We'll never ask for your password by email or phone. Only enter it here.
                </AlertBox>

                <Turnstile ref={turnstileRef} onVerify={setCaptchaToken} onExpire={() => setCaptchaToken(null)} />

                <button className="virsa-btn-primary" style={primaryBtn} disabled={loading || !captchaToken}>
                  {loading ? "Signing in…" : t("login.signIn")}
                </button>
              </form>
            </div>
          )}

          {/* ===================== SIGNUP ===================== */}
          {flow === "signup" && signupStage === "email" && (
            <div className="virsa-stage" key="signup-email">
              <h1 style={heading}>{t("login.createAccount")}</h1>
              <p style={subheading}>We'll email you a code to confirm it's you.</p>

              {error && (
                <AlertBox tone="error">
                  {error}
                  {error.includes("already uses this email") && (
                    <>
                      {" "}
                      <button
                        type="button"
                        style={{ ...linkBtn, textDecoration: "underline" }}
                        onClick={() => {
                          setLoginId(signupEmail);
                          setFlow("login");
                          setLoginStage("id");
                          resetMessages();
                          resetCaptcha();
                        }}
                      >
                        Sign in instead
                      </button>
                    </>
                  )}
                </AlertBox>
              )}

              <form onSubmit={once(sendSignupOtp)}>
                <label style={fieldLabel} htmlFor="signup-email">Email</label>
                <input
                  id="signup-email"
                  className="virsa-input"
                  style={input}
                  type="email"
                  autoFocus
                  placeholder="you@example.com"
                  value={signupEmail}
                  onChange={e => setSignupEmail(e.target.value)}
                />
                <label style={agreeRow}>
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={e => setAgreedToTerms(e.target.checked)}
                    style={agreeCheckbox}
                  />
                  <span>
                    I agree to the{" "}
                    <Link to="/terms" target="_blank" rel="noopener noreferrer" style={legalLink} onClick={e => e.stopPropagation()}>Terms</Link> and{" "}
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={legalLink} onClick={e => e.stopPropagation()}>Privacy Policy</Link>.
                  </span>
                </label>

                <Turnstile ref={turnstileRef} onVerify={setCaptchaToken} onExpire={() => setCaptchaToken(null)} />

                <button className="virsa-btn-primary" style={primaryBtn} disabled={loading || !agreedToTerms || !captchaToken}>
                  {loading ? "Sending code…" : "Send code →"}
                </button>
              </form>
            </div>
          )}

          {flow === "signup" && signupStage === "otp" && (
            <div className="virsa-stage" key="signup-otp">
              <button style={backLink} onClick={() => { setSignupStage("email"); resetMessages(); resetCaptcha(); }} disabled={loading}>
                ← Back
              </button>

              <h1 style={heading}>Enter the code</h1>
              <p style={subheading}>
                Sent to <strong>{signupEmail}</strong>. It expires shortly, so enter it soon.
              </p>

              {notice && <AlertBox tone="success">{notice}</AlertBox>}
              {error && <AlertBox tone="error">{error}</AlertBox>}

              <form onSubmit={once(verifySignupOtp)}>
                <label style={fieldLabel} htmlFor="signup-otp">6-digit code</label>
                <input
                  id="signup-otp"
                  className="virsa-input virsa-otp"
                  style={input}
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                  placeholder="······"
                  value={signupOtp}
                  onChange={e => setSignupOtp(e.target.value.replace(/\D/g, ""))}
                />
                <button className="virsa-btn-primary" style={primaryBtn} disabled={loading}>
                  {loading ? "Verifying…" : "Verify"}
                </button>
              </form>

              <Turnstile ref={turnstileRef} onVerify={setCaptchaToken} onExpire={() => setCaptchaToken(null)} />

              <button
                style={{ ...forgotLink, textAlign: "center", width: "100%" }}
                disabled={!captchaToken}
                onClick={once(sendSignupOtp)}
              >
                Resend code
              </button>
            </div>
          )}

          {flow === "signup" && signupStage === "birthdate" && (
            <div className="virsa-stage" key="signup-birthdate">
              <button style={backLink} onClick={() => { setSignupStage("otp"); resetMessages(); resetCaptcha(); }} disabled={loading}>
                ← Back
              </button>

              <h1 style={heading}>Date of birth</h1>
              <p style={subheading}>You must be 18 or older to use GatherYou. This is never shown to anyone.</p>

              {error && <AlertBox tone="error">{error}</AlertBox>}

              <form onSubmit={submitBirthdateStage}>
                <label style={fieldLabel} htmlFor="signup-birthdate">Date of birth</label>
                <div style={dobRow}>
                  <PickerSelect
                    id="signup-birthdate"
                    label="Day"
                    placeholder="Day"
                    style={{ flex: 0.85 }}
                    value={dobParts.d}
                    options={Array.from(
                      { length: daysInMonth(Number(dobParts.m) || 1, Number(dobParts.y) || 2000) },
                      (_, i) => ({ value: i + 1, label: String(i + 1) })
                    )}
                    onChange={v => changeDobPart("d", v)}
                  />

                  <PickerSelect
                    label="Month"
                    placeholder="Month"
                    style={{ flex: 1.6 }}
                    value={dobParts.m}
                    options={MONTH_NAMES.map((name, i) => ({ value: i + 1, label: name }))}
                    onChange={v => changeDobPart("m", v)}
                  />

                  <PickerSelect
                    label="Year"
                    placeholder="Year"
                    style={{ flex: 1.1 }}
                    value={dobParts.y}
                    initialScrollValue={new Date().getFullYear() - 25}
                    options={Array.from({ length: 101 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return { value: year, label: String(year) };
                    })}
                    onChange={v => changeDobPart("y", v)}
                  />
                </div>
                <button className="virsa-btn-primary" style={primaryBtn}>
                  Continue →
                </button>
              </form>
            </div>
          )}

          {flow === "signup" && signupStage === "username" && (
            <div className="virsa-stage" key="signup-username">
              <h1 style={heading}>Pick a username</h1>
              <p style={subheading}>This is how people on GatherYou will find you.</p>

              {error && <AlertBox tone="error">{error}</AlertBox>}

              <form onSubmit={submitUsernameStage}>
                <label style={fieldLabel} htmlFor="signup-username">Username</label>
                <input
                  id="signup-username"
                  className="virsa-input"
                  style={{
                    ...input,
                    border: `1.5px solid ${
                      usernameStatus === "available"
                        ? "var(--accent-success)"
                        : usernameStatus === "taken" || usernameStatus === "invalid"
                        ? "var(--accent-danger)"
                        : "var(--border-input)"
                    }`
                  }}
                  autoFocus
                  placeholder="choose_a_username"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                />

                <div style={usernameStatusLine} aria-live="polite">
                  {usernameStatus === "checking" && <span style={{ color: "var(--text-muted)" }}>Checking availability…</span>}
                  {usernameStatus === "available" && <span style={{ color: "var(--accent-success)" }}><IoCheckmark style={{ verticalAlign: -2 }} /> Username available</span>}
                  {usernameStatus === "taken" && <span style={{ color: "var(--accent-danger)" }}>That username is taken.</span>}
                  {usernameStatus === "error" && (
                    <span style={{ color: "var(--accent-danger)" }}>
                      Couldn't check that right now — check your connection and try again.
                    </span>
                  )}
                  {usernameStatus === "invalid" && username && (
                    <span style={{ color: "var(--accent-danger)" }}>
                      3–20 chars: lowercase letters, numbers, "." or "_"
                    </span>
                  )}
                </div>

                {usernameStatus === "taken" && usernameSuggestions.length > 0 && (
                  <div style={suggestionRow}>
                    {usernameSuggestions.map(s => (
                      <button
                        type="button"
                        key={s}
                        style={suggestionChip}
                        onClick={() => setUsername(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}

                <button className="virsa-btn-primary" style={primaryBtn} disabled={usernameStatus !== "available"}>
                  Continue →
                </button>
              </form>
            </div>
          )}

          {flow === "signup" && signupStage === "gender" && (
            <div className="virsa-stage" key="signup-gender">
              <button style={backLink} onClick={() => { setSignupStage("username"); resetMessages(); }} disabled={loading}>
                ← Back
              </button>

              <h1 style={heading}>Gender</h1>
              <p style={subheading}>Optional — kept private, never shown to anyone.</p>

              {error && <AlertBox tone="error">{error}</AlertBox>}

              <div style={genderOptionCol}>
                {GENDER_OPTIONS.map(opt => (
                  <button
                    type="button"
                    key={opt.value}
                    style={gender === opt.value ? genderOptionActive : genderOption}
                    onClick={() => setGender(opt.value)}
                  >
                    {opt.label}
                    {gender === opt.value && <IoCheckmark style={{ marginLeft: "auto" }} />}
                  </button>
                ))}
              </div>

              <button
                className="virsa-btn-primary"
                style={primaryBtn}
                disabled={loading}
                onClick={() => continueFromGenderStage(gender)}
              >
                {loading ? "Finishing…" : isOAuthAccount ? "Create account" : "Continue →"}
              </button>
              <button
                type="button"
                style={{ ...forgotLink, textAlign: "center", width: "100%" }}
                disabled={loading}
                onClick={() => { setGender(null); continueFromGenderStage(null); }}
              >
                Skip
              </button>
            </div>
          )}

          {flow === "signup" && signupStage === "password" && (
            <div className="virsa-stage" key="signup-password">
              <button style={backLink} onClick={() => { setSignupStage("gender"); resetMessages(); }} disabled={loading}>
                ← Back
              </button>

              <h1 style={heading}>Create a password</h1>
              <p style={subheading}>@{username.trim().toLowerCase()} · {PASSWORD_REQUIREMENTS_TEXT}</p>

              {error && <AlertBox tone="error">{error}</AlertBox>}

              <form onSubmit={once(completeSignup)}>
                <label style={fieldLabel} htmlFor="signup-password">Password</label>
                <div style={passwordWrap}>
                  <input
                    id="signup-password"
                    className="virsa-input"
                    style={{ ...input, marginBottom: 0 }}
                    type={showPassword ? "text" : "password"}
                    autoFocus
                    placeholder="At least 6 characters"
                    value={signupPassword}
                    onChange={e => setSignupPassword(e.target.value)}
                  />
                  <button type="button" style={eyeBtn} onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? <IoEyeOffOutline /> : <IoEyeOutline />}
                  </button>
                </div>

                <AlertBox tone="security">
                  Choose a password you don't use anywhere else.
                </AlertBox>

                <button className="virsa-btn-primary" style={primaryBtn} disabled={loading}>
                  {loading ? "Creating account…" : "Create account"}
                </button>
              </form>
            </div>
          )}

          {flow === "signup" && signupStage === "done" && (
            <div className="virsa-stage" style={{ textAlign: "center" }} key="signup-done">
              <div style={{ fontSize: 36, marginBottom: 6, color: "var(--accent-success)" }}><IoCheckmarkCircle /></div>
              <h1 style={heading}>Welcome, @{username.trim().toLowerCase()}</h1>
              <p style={subheading}>Your account has been created.</p>
              <button className="virsa-btn-primary" style={primaryBtn} onClick={() => navigate("/")}>
                Go to GatherYou
              </button>
            </div>
          )}

          {/* ===================== RECOVERY ===================== */}
          {flow === "recovery" && recoveryStage === "email" && (
            <div className="virsa-stage" key="recovery-email">
              <button style={backLink} onClick={backToLoginFromRecovery} disabled={loading}>← Back to sign in</button>

              <h1 style={heading}>Recover your account</h1>
              <p style={subheading}>We'll send a code to your email.</p>

              {error && <AlertBox tone="error">{error}</AlertBox>}

              <form onSubmit={once(sendRecoveryOtp)}>
                <label style={fieldLabel} htmlFor="recovery-email">Email</label>
                <input
                  id="recovery-email"
                  className="virsa-input"
                  style={input}
                  type="email"
                  autoFocus
                  placeholder="you@example.com"
                  value={recoveryEmail}
                  onChange={e => setRecoveryEmail(e.target.value)}
                />

                <Turnstile ref={turnstileRef} onVerify={setCaptchaToken} onExpire={() => setCaptchaToken(null)} />

                <button className="virsa-btn-primary" style={primaryBtn} disabled={loading || !captchaToken}>
                  {loading ? "Sending code…" : "Send code →"}
                </button>
              </form>
            </div>
          )}

          {flow === "recovery" && recoveryStage === "otp" && (
            <div className="virsa-stage" key="recovery-otp">
              <button style={backLink} onClick={() => { setRecoveryStage("email"); resetMessages(); resetCaptcha(); }} disabled={loading}>
                ← Back
              </button>

              <h1 style={heading}>Enter the code</h1>
              <p style={subheading}>Sent to <strong>{recoveryEmail}</strong>.</p>

              {notice && <AlertBox tone="success">{notice}</AlertBox>}
              {error && <AlertBox tone="error">{error}</AlertBox>}

              <form onSubmit={once(verifyRecoveryOtp)}>
                <label style={fieldLabel} htmlFor="recovery-otp">6-digit code</label>
                <input
                  id="recovery-otp"
                  className="virsa-input virsa-otp"
                  style={input}
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                  placeholder="······"
                  value={recoveryOtp}
                  onChange={e => setRecoveryOtp(e.target.value.replace(/\D/g, ""))}
                />
                <button className="virsa-btn-primary" style={primaryBtn} disabled={loading}>
                  {loading ? "Verifying…" : "Verify"}
                </button>
              </form>

              <Turnstile ref={turnstileRef} onVerify={setCaptchaToken} onExpire={() => setCaptchaToken(null)} />

              <button
                style={{ ...forgotLink, textAlign: "center", width: "100%" }}
                disabled={!captchaToken}
                onClick={once(sendRecoveryOtp)}
              >
                Resend code
              </button>
            </div>
          )}

          {flow === "recovery" && recoveryStage === "newpassword" && (
            <div className="virsa-stage" key="recovery-newpassword">
              <h1 style={heading}>Set a new password</h1>
              <p style={subheading}>{PASSWORD_REQUIREMENTS_TEXT}</p>

              {error && <AlertBox tone="error">{error}</AlertBox>}

              <form onSubmit={once(submitNewPassword)}>
                <label style={fieldLabel} htmlFor="recovery-newpass">New password</label>
                <div style={passwordWrap}>
                  <input
                    id="recovery-newpass"
                    className="virsa-input"
                    style={{ ...input, marginBottom: 0 }}
                    type={showPassword ? "text" : "password"}
                    autoFocus
                    placeholder="New password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                  />
                  <button type="button" style={eyeBtn} onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? <IoEyeOffOutline /> : <IoEyeOutline />}
                  </button>
                </div>

                <button className="virsa-btn-primary" style={{ ...primaryBtn, marginTop: 14 }} disabled={loading}>
                  {loading ? "Saving…" : "Save new password"}
                </button>
              </form>
            </div>
          )}

          {flow === "recovery" && recoveryStage === "done" && (
            <div className="virsa-stage" style={{ textAlign: "center" }} key="recovery-done">
              <div style={{ fontSize: 36, marginBottom: 6, color: "var(--accent-success)" }}><IoCheckmarkCircle /></div>
              <h1 style={heading}>Password updated</h1>
              <p style={subheading}>You're all set.</p>
              <button className="virsa-btn-primary" style={primaryBtn} onClick={() => navigate("/")}>
                Go to GatherYou
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===== Small building blocks ===== */

function AlertBox({ tone, children }) {
  const palette = {
    error: { bg: "var(--alert-error-bg)", border: "var(--accent-danger)", text: "var(--alert-error-text)", Icon: IoWarningOutline },
    success: { bg: "var(--alert-success-bg)", border: "var(--accent-success)", text: "var(--alert-success-text)", Icon: IoCheckmarkCircle },
    security: { bg: "var(--alert-info-bg)", border: "var(--accent-link)", text: "var(--alert-info-text)", Icon: IoShieldCheckmarkOutline }
  }[tone];

  return (
    <div
      role={tone === "error" ? "alert" : undefined}
      style={{
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        background: palette.bg,
        border: `1px solid ${palette.border}33`,
        borderLeft: `3px solid ${palette.border}`,
        color: palette.text,
        borderRadius: 8,
        padding: "10px 12px",
        fontSize: 13,
        lineHeight: 1.4,
        margin: "10px 0 16px"
      }}
    >
      <span aria-hidden="true" style={{ display: "flex", fontSize: 16, flexShrink: 0 }}>
        <palette.Icon />
      </span>
      <span>{children}</span>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
    </svg>
  );
}

function FlowerMotif() {
  const petals = 6;
  return (
    <svg width="92" height="92" viewBox="0 0 92 92" style={{ position: "absolute" }}>
      {Array.from({ length: petals }).map((_, i) => {
        const angle = (360 / petals) * i;
        return (
          <ellipse
            key={i}
            cx="46"
            cy="18"
            rx="8"
            ry="14"
            fill={i % 2 === 0 ? "var(--accent-gold)" : "var(--accent-primary)"}
            opacity="0.9"
            transform={`rotate(${angle} 46 46)`}
          />
        );
      })}
      <circle cx="46" cy="46" r="9" fill="var(--accent-link)" />
    </svg>
  );
}

/* ===== STYLES ===== */

const page = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--bg-page)",
  fontFamily: "'Inter', Arial, sans-serif",
  padding: "20px 20px calc(90px + env(safe-area-inset-bottom))"
};

const card = {
  width: "100%",
  maxWidth: 400,
  background: "var(--bg-surface)",
  borderRadius: 18,
  boxShadow: "0 24px 60px rgba(74, 34, 24, 0.18)",
  overflow: "hidden",
  border: "1px solid var(--border-medium)"
};

const trimRow = { display: "flex", width: "100%" };
const trimDiamond = { flex: 1, height: 7 };
const cardBody = { padding: "30px 32px 34px" };

const brandRow = { display: "flex", alignItems: "center", gap: 10, marginBottom: 18 };
const brandIcon = { display: "block", objectFit: "contain", borderRadius: 8, flexShrink: 0 };
const brandMark = { fontFamily: "'Fraunces', serif", fontStyle: "italic", fontWeight: 600, fontSize: 22, color: "var(--accent-primary)" };
const tabRow = { display: "flex", gap: 4, background: "var(--bg-soft)", borderRadius: 10, padding: 4, marginBottom: 22 };
const tabBtn = {
  flex: 1,
  padding: "8px 0",
  border: "none",
  background: "transparent",
  borderRadius: 8,
  fontSize: 13.5,
  fontWeight: 600,
  color: "var(--text-muted)",
  cursor: "pointer"
};
const tabBtnActive = { background: "var(--bg-surface)", color: "var(--accent-primary)", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" };

const googleBtn = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  padding: "11px 14px",
  borderRadius: 10,
  border: "1px solid var(--border-soft)",
  background: "var(--bg-surface)",
  color: "var(--text-heading)",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  marginBottom: 16
};

const dividerRow = { display: "flex", alignItems: "center", gap: 10, marginBottom: 16 };
const dividerLine = { flex: 1, height: 1, background: "var(--border-soft)" };
const dividerText = { fontSize: 12, color: "var(--text-muted)", fontWeight: 600 };

const heading = { fontFamily: "'Fraunces', serif", fontSize: 25, fontWeight: 600, color: "var(--text-heading)", margin: "0 0 6px" };
const subheading = { fontSize: 14, color: "var(--text-body)", margin: "0 0 20px", lineHeight: 1.5 };

const fieldLabel = { display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 };

const input = {
  width: "100%",
  padding: "12px 14px",
  fontSize: 14,
  borderRadius: 10,
  border: "1.5px solid var(--border-input)",
  background: "var(--bg-surface)",
  color: "var(--text-heading)",
  outline: "none",
  marginBottom: 14
};

const dobRow = { display: "flex", gap: 8, marginBottom: 14 };

const passwordWrap = { position: "relative", marginBottom: 14 };
const eyeBtn = {
  position: "absolute",
  right: 10,
  top: "50%",
  transform: "translateY(-50%)",
  display: "flex",
  background: "none",
  border: "none",
  color: "var(--text-muted)",
  cursor: "pointer",
  fontSize: 18
};

const primaryBtn = {
  width: "100%",
  padding: "13px 14px",
  borderRadius: 10,
  border: "none",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  fontSize: 14.5,
  fontWeight: 700,
  cursor: "pointer",
  marginTop: 4
};

const backLink = { background: "none", border: "none", color: "var(--text-body)", fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 18 };

const motifWrap = { position: "relative", width: 92, height: 92, margin: "0 auto 14px", display: "flex", alignItems: "center", justifyContent: "center" };
const avatarCircle = {
  width: 46,
  height: 46,
  borderRadius: "50%",
  background: "var(--brand-mark-bg)",
  color: "var(--brand-mark-text)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "'Fraunces', serif",
  fontSize: 18,
  fontWeight: 600,
  zIndex: 1
};

const idChip = {
  display: "block",
  width: "100%",
  textAlign: "center",
  background: "var(--bg-soft)",
  border: "1px solid var(--border-input)",
  borderRadius: 20,
  padding: "8px 14px",
  fontSize: 13.5,
  color: "var(--text-secondary)",
  cursor: "pointer",
  marginBottom: 6
};

const forgotLink = {
  display: "block",
  background: "none",
  border: "none",
  color: "var(--accent-link)",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  padding: 0,
  marginBottom: 4,
  marginTop: 6,
  textAlign: "right",
  width: "100%"
};

const linkBtn = {
  background: "none",
  border: "none",
  color: "var(--accent-link)",
  fontWeight: 700,
  fontSize: "inherit",
  cursor: "pointer",
  padding: 0
};

const usernameStatusLine = { fontSize: 12.5, marginTop: -8, marginBottom: 14, minHeight: 16 };

const suggestionRow = { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 };
const suggestionChip = {
  background: "var(--bg-soft)",
  border: "1px solid var(--border-input)",
  borderRadius: 16,
  padding: "5px 12px",
  fontSize: 12.5,
  color: "var(--text-secondary)",
  cursor: "pointer"
};

const agreeRow = {
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
  fontSize: 12.5,
  color: "var(--text-secondary)",
  margin: "14px 0 4px",
  lineHeight: 1.5,
  cursor: "pointer"
};

const genderOptionCol = { display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 };
const genderOption = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  padding: "12px 14px",
  borderRadius: 10,
  border: "1.5px solid var(--border-input)",
  background: "var(--bg-surface)",
  color: "var(--text-heading)",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  textAlign: "left"
};
const genderOptionActive = {
  ...genderOption,
  border: "1.5px solid var(--accent-primary)",
  background: "var(--bg-soft)",
  color: "var(--accent-primary)"
};

const legalLink = {
  color: "var(--accent-link)",
  fontWeight: 600,
  textDecoration: "none"
};

const agreeCheckbox = {
  width: 16,
  height: 16,
  minWidth: 16,
  marginTop: 2,
  accentColor: "var(--accent-primary)",
  cursor: "pointer"
};
